"use client";

/**
 * A última rede: um erro no próprio layout de raiz.
 *
 * O `global-error` do Next SUBSTITUI o root layout em vez de ser renderizado
 * dentro dele — que é a única forma de sobreviver a um erro que aconteça no
 * próprio layout. Daí as duas consequências abaixo, ambas obrigatórias:
 *
 *   1. Tem de renderizar `<html>` e `<body>` com as suas mãos. Não há nenhum
 *      à volta deste componente.
 *   2. Tem de importar `globals.css`, porque o import que está no root layout
 *      não corre. Sem ele, o ecrã de erro aparecia sem estilo nenhum — e a
 *      última coisa que se quer mostrar a quem já apanhou um erro é uma página
 *      que parece ainda mais avariada.
 *
 *   3. Tem de repor o tema e a densidade no `<html>` com as suas mãos, pelo
 *      mesmo motivo. Não há `ThemeProvider` acima deste componente, e sem a
 *      classe `dark` o ecrã de erro aparecia branco a quem tem a app escura —
 *      a app parecer OUTRA aplicação é o pior momento possível para o fazer.
 *
 * O tema é reposto por script inline e não por `<ThemeProvider>` de propósito.
 * Este é o código que corre quando o resto está partido — vale a mesma regra
 * que o `CrashScreen` segue no comentário dele: quantos menos componentes no
 * caminho, menos coisas que podem estar partidas também. Um script inline não
 * hidrata, não depende de nada e corre antes da primeira pintura.
 *
 * Na prática este ficheiro quase nunca dispara: o `[tenant]/error.tsx` apanha
 * tudo o que acontece dentro da aplicação (e esse herda o tema, porque vive
 * dentro do root layout). Existe para o resto.
 */
import "./globals.css";
import { CrashScreen } from "@/components/bug-report/CrashScreen";
import {
  DEFAULT_DENSITY,
  DENSITY_ORDER,
  DENSITY_STORAGE_KEY,
} from "@/lib/design/tokens";

// A chave e os valores do next-themes tal como o root layout o configura:
// `attribute="class"`, `defaultTheme="system"`, `enableSystem`. "system" e a
// ausência de preferência resolvem-se os dois pelo `prefers-color-scheme`.
const THEME_SCRIPT = `(function(){try{var t=localStorage.getItem("theme");var dark=t==="dark"||((!t||t==="system")&&window.matchMedia("(prefers-color-scheme: dark)").matches);document.documentElement.classList.toggle("dark",dark);document.documentElement.style.colorScheme=dark?"dark":"light";}catch(e){}})();`;

const DENSITY_SCRIPT = `(function(){try{var d=localStorage.getItem(${JSON.stringify(DENSITY_STORAGE_KEY)});var v=${JSON.stringify(DENSITY_ORDER)};document.documentElement.dataset.density=v.indexOf(d)>-1?d:${JSON.stringify(DEFAULT_DENSITY)};}catch(e){document.documentElement.dataset.density=${JSON.stringify(DEFAULT_DENSITY)};}})();`;

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  return (
    <html lang="pt" suppressHydrationWarning>
      <head>
        <script
          dangerouslySetInnerHTML={{
            __html: `${THEME_SCRIPT}${DENSITY_SCRIPT}`,
          }}
        />
      </head>
      {/* Aqui não há layout: o `<body>` é o único sítio onde a altura do ecrã
          pode entrar. `h-screen` e não `min-h-screen` de propósito — o
          `min-h-full` do CrashScreen é uma percentagem, e uma percentagem só
          resolve contra uma altura DEFINIDA; com `min-h-screen` a altura do
          body continua `auto` e o cartão ficava encostado ao topo. */}
      <body className="h-screen">
        <CrashScreen error={error} reset={reset} homeHref="/" />
      </body>
    </html>
  );
}
